import { MoreHorizontal, MoreVertical, Trash2 } from 'lucide-react';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import { Button } from './button.js';
import { cn } from './cn.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu.js';

export type ResourceAction = {
  label: string;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

export function ResourceActionsMenu({
  actions,
  orientation = 'horizontal',
  label,
}: {
  actions: readonly ResourceAction[];
  orientation?: 'horizontal' | 'vertical';
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const activateOnClickRef = useRef(false);

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={label}
          onClick={(event) => {
            const activateOnClick = activateOnClickRef.current && event.detail !== 0;
            activateOnClickRef.current = false;
            if (activateOnClick) setOpen((current) => !current);
          }}
          onPointerCancel={() => {
            activateOnClickRef.current = false;
          }}
          onPointerDown={(event) => {
            activateOnClickRef.current = event.pointerType !== 'mouse';
            if (activateOnClickRef.current) event.preventDefault();
          }}
          size="icon"
          variant="ghost"
        >
          {orientation === 'vertical' ? (
            <MoreVertical aria-hidden="true" />
          ) : (
            <MoreHorizontal aria-hidden="true" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            {...(action.destructive ? { destructive: true } : {})}
            {...(action.disabled === undefined ? {} : { disabled: action.disabled })}
            onSelect={action.onSelect}
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const REVEAL_WIDTH = 88;
const INTENT_THRESHOLD = 8;

let activeSwipeRow: { id: string; root: HTMLDivElement; close: () => void } | undefined;
let outsideListenerInstalled = false;

function ensureOutsideListener() {
  if (outsideListenerInstalled) return;
  document.addEventListener('pointerdown', (event) => {
    if (activeSwipeRow && !activeSwipeRow.root.contains(event.target as Node)) {
      activeSwipeRow.close();
      activeSwipeRow = undefined;
    }
  });
  outsideListenerInstalled = true;
}

type Gesture = {
  pointerId: number;
  startX: number;
  startY: number;
  origin: number;
  intent: 'pending' | 'horizontal' | 'vertical';
};

export function SwipeRevealAction({
  actionAccessibleLabel,
  actionLabel,
  children,
  className,
  disabled = false,
  onAction,
}: {
  actionAccessibleLabel?: string;
  actionLabel: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onAction: () => void;
}) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const suppressClickRef = useRef(false);
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  const open = offset !== 0;

  useEffect(() => {
    ensureOutsideListener();
    return () => {
      if (activeSwipeRow?.id === id) activeSwipeRow = undefined;
    };
  }, [id]);

  const updateOffset = (next: number) => {
    offsetRef.current = next;
    setOffset(next);
  };

  const close = () => {
    updateOffset(0);
    if (activeSwipeRow?.id === id) activeSwipeRow = undefined;
  };

  const finishGesture = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (gesture.intent === 'horizontal') {
      const next = offsetRef.current <= -REVEAL_WIDTH / 2 ? -REVEAL_WIDTH : 0;
      updateOffset(next);
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      if (next !== 0 && rootRef.current) {
        if (activeSwipeRow?.id !== id) activeSwipeRow?.close();
        activeSwipeRow = { id, root: rootRef.current, close };
      } else if (activeSwipeRow?.id === id) {
        activeSwipeRow = undefined;
      }
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
    gestureRef.current = null;
  };

  return (
    <div
      ref={rootRef}
      className={cn('relative overflow-hidden rounded-md', className)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') close();
      }}
    >
      <button
        aria-label={actionAccessibleLabel ?? actionLabel}
        aria-hidden={!open}
        className="absolute inset-y-0 right-0 grid w-[88px] place-items-center bg-err px-2 text-xs font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-ring disabled:opacity-45"
        disabled={disabled}
        onClick={() => {
          close();
          onAction();
        }}
        tabIndex={open ? 0 : -1}
        type="button"
      >
        <span className="grid justify-items-center gap-1">
          <Trash2 aria-hidden="true" className="size-4" />
          {actionLabel}
        </span>
      </button>
      <div
        className="relative z-10 touch-pan-y transition-transform duration-200 ease-out motion-reduce:transition-none"
        onClickCapture={(event) => {
          if (!suppressClickRef.current) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onPointerCancel={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          gestureRef.current = null;
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
          close();
        }}
        onPointerDown={(event) => {
          if (disabled || event.pointerType === 'mouse' || !event.isPrimary) return;
          gestureRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            origin: offsetRef.current,
            intent: 'pending',
          };
        }}
        onPointerMove={(event) => {
          const gesture = gestureRef.current;
          if (!gesture || gesture.pointerId !== event.pointerId) return;
          const deltaX = event.clientX - gesture.startX;
          const deltaY = event.clientY - gesture.startY;
          if (gesture.intent === 'pending') {
            if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < INTENT_THRESHOLD) return;
            gesture.intent = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
            if (gesture.intent === 'horizontal') {
              event.currentTarget.setPointerCapture(event.pointerId);
            }
          }
          if (gesture.intent !== 'horizontal') return;
          event.preventDefault();
          updateOffset(Math.max(-REVEAL_WIDTH, Math.min(0, gesture.origin + deltaX)));
        }}
        onPointerUp={finishGesture}
        style={{ transform: `translateX(${offset}px)` }}
      >
        {children}
      </div>
    </div>
  );
}
